import React, { useState, useEffect, useRef } from "react";
import { BiPlus, BiDotsVerticalRounded } from "react-icons/bi";
import { FaArrowUp, FaArrowDown, FaAngleLeft } from "react-icons/fa";
import KanbanItem from "./KanbanItem";
import Modal from "../../../../../components/modal";
import toast from "react-hot-toast";
import api from "../../../../../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import CreateTicket from "./createTicket";

const columns = [
  {
    id: "TODO",
    title: "To Do",
  },
  {
    id: "READY_TO_DEV",
    title: "Ready to Dev",
  },
  {
    id: "IN_PROGRESS",
    title: "In Progress",
  },
  {
    id: "TESTING",
    title: "Testing",
  },
  {
    id: "DONE",
    title: "Done",
  },
  {
    id: "ARCHIVED",
    title: "Archived",
  },
];

export function KanbanColumn({
  column,
  items,
  loading,
  dragOverColumn,
  dragOverItemIndex,
  onDragOver,
  onDrop,
  onDragStart,
  onItemDragOver,
  onDragEnd,
  onDeleteItem,
  onRemoveFromBacklog,
  draggedItem,
  draggedRoadmapItem,
  selectedItem,
  project,
  onTicketAdded,
  onMoveAllItems,
}) {
  const isOver = dragOverColumn === column.id;
  const isDraggingAnyItem = draggedItem || draggedRoadmapItem;
  const [autoScrolling, setAutoScrolling] = useState(null);
  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState({ field: "priority", direction: "asc" });
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [showMoveAllSubmenu, setShowMoveAllSubmenu] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const sortButtonRef = useRef(null);
  const sortMenuRef = useRef(null);

  // Cleanup scroll interval on unmount
  useEffect(() => {
    return () => {
      if (autoScrolling) {
        clearInterval(autoScrolling);
      }
    };
  }, [autoScrolling]);

  // Updated: Sorting logic based on sortBy state (field and direction)
  const sortedItems = loading
    ? [] // Don't sort if loading
    : [...items].sort((a, b) => {
        let comparison = 0;
        const field = sortBy.field;
        const direction = sortBy.direction === "asc" ? 1 : -1;

        if (field === "position") {
          const posA = a.position !== undefined && a.position !== null ? a.position : Infinity; // Use Infinity for null/undefined
          const posB = b.position !== undefined && b.position !== null ? b.position : Infinity;
          comparison = posA - posB;
        } else if (field === "priority") {
          const priorityA = a.priority || "";
          const priorityB = b.priority || "";
          // Handle cases where priority might be missing
          if (!priorityA && !priorityB) comparison = 0;
          else if (!priorityA) comparison = 1; // a goes after b if it lacks priority
          else if (!priorityB) comparison = -1; // a goes before b if b lacks priority
          else comparison = priorityA.localeCompare(priorityB);
        } else if (field === "module") {
          const moduleA = a.module_name || "";
          const moduleB = b.module_name || "";
          comparison = moduleA.localeCompare(moduleB);
        } else if (field === "category") {
          const categoryA = a.category || "";
          const categoryB = b.category || "";
          comparison = categoryA.localeCompare(categoryB);
        } else if (field === "createdAt") {
          const createdAtA = a.createdAt || "";
          const createdAtB = b.createdAt || "";
          comparison = createdAtA.localeCompare(createdAtB);
        }

        return comparison * direction;
      });

  // Handle vertical scrolling within the column
  const handleColumnDragOver = (e) => {
    e.preventDefault();

    // Call the parent onDragOver handler
    onDragOver(e, column.id);

    // Check for both draggedItem (from current column) and draggedRoadmapItem (from roadmap)
    if (!draggedItem && !draggedRoadmapItem) return;

    // Get the items container element
    const itemsContainer = e.currentTarget.querySelector(".kanban-items-container");
    if (!itemsContainer) return;

    const containerRect = itemsContainer.getBoundingClientRect();

    // Vertical scroll detection - margins for top and bottom edges
    const scrollMargin = 120; // px from edge to trigger scrolling
    const scrollSpeed = 15; // px per scroll interval

    // Clear any existing auto-scroll interval
    if (autoScrolling) {
      clearInterval(autoScrolling);
      setAutoScrolling(null);
    }

    // Check if we're close to the top edge
    if (e.clientY < containerRect.top + scrollMargin) {
      const intervalId = setInterval(() => {
        itemsContainer.scrollTop -= scrollSpeed;
      }, 30);
      setAutoScrolling(intervalId);
    }
    // Check if we're close to the bottom edge
    else if (e.clientY > containerRect.bottom - scrollMargin) {
      const intervalId = setInterval(() => {
        itemsContainer.scrollTop += scrollSpeed;
      }, 30);
      setAutoScrolling(intervalId);
    }
  };

  const handleDragEnd = (e) => {
    // Clear scrolling interval when drag ends
    if (autoScrolling) {
      clearInterval(autoScrolling);
      setAutoScrolling(null);
    }

    // Call the parent onDragEnd handler
    onDragEnd(e);
  };

  // Added useEffect for click outside detection
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the click is outside the menu AND outside the button
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target) && sortButtonRef.current && !sortButtonRef.current.contains(event.target)) {
        setIsSortMenuOpen(false);
      }
    }

    // Add listener only when the menu is open
    if (isSortMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      // Remove listener if menu is closed (or on initial render)
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove listener on unmount or when menu closes
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortMenuOpen]); // Dependency array ensures effect runs when isSortMenuOpen changes

  const handleSortChange = (field, direction) => {
    setSortBy({ field, direction });
    setIsSortMenuOpen(false);
  };

  const handleTicketCreated = (newTicket) => {
    setIsCreateTicketModalOpen(false);

    query.set("item_modal_id", newTicket._id);
    navigate({ search: query.toString() });
    onTicketAdded(newTicket, column.id);
  };

  const handleMoveAllClick = async (targetStatusId) => {
    const itemsToProcess = [...items];

    if (!itemsToProcess || itemsToProcess.length === 0) return;

    try {
      const updatePromises = itemsToProcess.map((item) => api.put(`/roadlog_item/${item._id}`, { status: targetStatusId }));

      const results = await Promise.all(updatePromises);

      results.forEach(({ ok }, index) => {
        if (!ok) toast.error(`Failed to move "${itemsToProcess[index].title || itemsToProcess[index]._id}" to ${targetStatusId}.`);
      });
      toast.success(`All items attempted to move to ${targetStatusId}.`);
    } catch (error) {
      console.error("Error moving all items:", error);
      toast.error(`An error occurred while moving items to ${targetStatusId}.`);
    } finally {
      onMoveAllItems();
      setShowMoveAllSubmenu(false);
      setIsSortMenuOpen(false);
    }
  };

  // Define sortable fields for the menu
  const sortOptions = [
    { key: "position", label: "Position" },
    { key: "priority", label: "Priority" },
    { key: "module", label: "Module" },
    { key: "category", label: "Category" },
    { key: "createdAt", label: "Creation date" },
  ];

  useEffect(() => {
    const handleGlobalDragEnd = () => {
      if (autoScrolling) {
        clearInterval(autoScrolling);
        setAutoScrolling(null);
      }
    };

    const handleClearAutoScroll = () => {
      if (autoScrolling) {
        clearInterval(autoScrolling);
        setAutoScrolling(null);
      }
    };

    document.addEventListener("dragend", handleGlobalDragEnd);
    document.addEventListener("drop", handleGlobalDragEnd);
    document.addEventListener("clearColumnAutoScroll", handleClearAutoScroll);

    return () => {
      if (autoScrolling) {
        clearInterval(autoScrolling);
      }
      document.removeEventListener("dragend", handleGlobalDragEnd);
      document.removeEventListener("drop", handleGlobalDragEnd);
      document.removeEventListener("clearColumnAutoScroll", handleClearAutoScroll);
    };
  }, [autoScrolling]);

  return (
    <div
      className={`flex flex-col rounded-lg min-w-[350px] ${isOver && dragOverItemIndex === null ? "bg-gray-100" : ""}`}
      onDragOver={handleColumnDragOver}
      onDrop={(e) => onDrop(e, column.id)}>
      <div className="flex items-center justify-between p-3 border-b border-gray-200 relative">
        <div className="flex items-center">
          <div className={`w-2 h-2 rounded-full ${column.color} mr-2`}></div>
          <h3 className="font-medium text-gray-700">{column.title}</h3>
          {!loading && items.length > 0 && <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">{items.length}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setIsCreateTicketModalOpen(true)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-0.5">
            <BiPlus size={18} />
          </button>
          <div className="relative flex items-center gap-2" ref={sortButtonRef}>
            <button className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-0.5" onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}>
              <BiDotsVerticalRounded size={18} />
            </button>
            {isSortMenuOpen && (
              <div ref={sortMenuRef} className="absolute right-0 top-5 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                <ul className="py-1">
                  <li
                    className="relative"
                    onMouseEnter={() => {
                      if (items.length > 0) setShowMoveAllSubmenu(true);
                    }}
                    onMouseLeave={() => setShowMoveAllSubmenu(false)}>
                    <div
                      className={`flex items-center px-2 py-2 text-sm text-gray-700 ${
                        items.length === 0 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 cursor-pointer"
                      }`}>
                      {items.length > 0 && <FaAngleLeft className="mr-2 text-gray-400" size={12} />}
                      <span>Move all items</span>
                    </div>

                    {showMoveAllSubmenu && items.length > 0 && (
                      <div className="absolute right-full top-0 -mt-px w-48 bg-white rounded-md shadow-lg z-30 border border-gray-200">
                        <ul className="py-1">
                          {columns
                            .filter((status) => status.id !== column.id)
                            .map((status) => (
                              <li key={status.id} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer" onClick={() => handleMoveAllClick(status.id)}>
                                {status.title}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </li>
                  <hr className="my-1 border-t border-gray-200" />

                  {sortOptions.map((option) => {
                    const isActiveField = sortBy.field === option.key;
                    const isAscActive = isActiveField && sortBy.direction === "asc";
                    const isDescActive = isActiveField && sortBy.direction === "desc";

                    return (
                      <li key={option.key} className={`flex justify-between items-center px-4 py-2 text-sm text-gray-700 ${isActiveField ? "font-semibold" : ""}`}>
                        <span>{option.label}</span>
                        <div className="flex items-center space-x-2 ml-2">
                          <span
                            className={`cursor-pointer p-1 rounded hover:bg-gray-200 ${isAscActive ? "text-blue-600 bg-gray-100" : "text-gray-400 hover:text-gray-600"}`}
                            onClick={() => handleSortChange(option.key, "asc")}>
                            <FaArrowUp size={12} />
                          </span>
                          <span
                            className={`cursor-pointer p-1 rounded hover:bg-gray-200 ${isDescActive ? "text-blue-600 bg-gray-100" : "text-gray-400 hover:text-gray-600"}`}
                            onClick={() => handleSortChange(option.key, "desc")}>
                            <FaArrowDown size={12} />
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pt-2 kanban-items-container">
        {loading ? (
          // Render skeletons if loading
          <>
            <KanbanItemSkeleton />
            <KanbanItemSkeleton />
            <KanbanItemSkeleton />
            <KanbanItemSkeleton />
          </>
        ) : (
          // Render actual items if not loading
          <>
            {sortedItems.map((item, index) => (
              <React.Fragment key={item._id}>
                {isOver && dragOverItemIndex === index && isDraggingAnyItem && <div className="h-1 bg-blue-500 rounded my-1 transition-all duration-300 animate-pulse" />}

                <KanbanItem
                  item={item}
                  column={column}
                  itemIndex={index}
                  onDragStart={onDragStart}
                  onDragOver={(e) => onItemDragOver(e, column.id, index)}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => onDrop(e, column.id, index)}
                  isDragging={draggedItem?.item?._id === item._id}
                  onDeleteItem={onDeleteItem}
                  onRemoveFromBacklog={onRemoveFromBacklog}
                  selectedItem={selectedItem}
                />

                {isOver && dragOverItemIndex === index + 1 && isDraggingAnyItem && <div className="h-1 bg-blue-500 rounded my-1 transition-all duration-300 animate-pulse" />}
              </React.Fragment>
            ))}

            {items.length === 0 && isOver && isDraggingAnyItem && <div className="border-2 border-dashed border-blue-300 rounded my-2 p-3 bg-blue-50 animate-pulse"></div>}
          </>
        )}
      </div>
      <CreateTicket isOpen={isCreateTicketModalOpen} onClose={() => setIsCreateTicketModalOpen(false)} project={project} onTicketCreated={handleTicketCreated} status={column.id} />
    </div>
  );
}

// Simple Skeleton Component
const KanbanItemSkeleton = () => (
  <div className="animate-pulse bg-white border border-gray-200 rounded-lg p-3 mb-2 shadow-sm h-[76px]">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
  </div>
);

export default KanbanColumn;
